import { isNativePlatform } from '@/utils/capacitorPlatform';
import router from '@/router';
import store from '@/store';
import { api } from '@/utils/apiService';

const handlePushAction = (action, { navigate = false } = {}) => {
  if (!action) return;
  try {
    const parsed = typeof action === 'string' ? JSON.parse(action) : action;
    if (parsed.page === 'bewertung') {
      store.dispatch('review/fetchStats').catch(() => {});
      if (navigate) {
        router.push('/navigation/bewertung');
      }
    }
    if (parsed.page === 'profil') {
      store.dispatch('review/fetchNotifications').catch(() => {});
      if (navigate) {
        router.push('/navigation/profil');
      }
    }
    if (parsed.page === 'zubereitung') {
      import('@/utils/liveSync').then(({ refreshCompanyLiveCounts }) => {
        refreshCompanyLiveCounts(store).catch(() => {});
      });
      if (navigate) {
        if (store.getters['profile/isLoggedIn']) {
          router.push('/navigation/zubereitung');
          return;
        }
        import('@/utils/kitchenKiosk').then(({ isKitchenKioskActive }) => {
          if (isKitchenKioskActive()) {
            router.push('/kueche');
          } else {
            router.push('/navigation/zubereitung');
          }
        });
      }
    }
  } catch (e) {
    console.warn('customer push action parse failed', e);
  }
};

async function ensureFirebaseMessagingSw() {
  if (!('serviceWorker' in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (existing) return existing;
    return navigator.serviceWorker.register('/firebase-messaging-sw.js');
  } catch (e) {
    console.error('customer firebase-messaging-sw registration', e);
    return null;
  }
}

export default {
  namespaced: true,
  state: {
    pushNotificationsInitialized: false,
    userChannelCreated: false,
    messageToken: '',
  },
  mutations: {
    SET_PUSH_NOTIFICATION_INITIALIZED(state, value) {
      state.pushNotificationsInitialized = value;
    },
    SET_USER_CHANNEL_CREATED(state, value) {
      state.userChannelCreated = value;
    },
    SET_MESSAGE_TOKEN(state, token) {
      state.messageToken = token;
    },
  },
  getters: {
    getMessageToken: (state) => state.messageToken,
    getPushNotificationsInitialized: (state) => state.pushNotificationsInitialized,
    getUserChannelCreated: (state) => state.userChannelCreated,
  },
  actions: {
    initPushNotifications: async ({ getters, dispatch }) => {
      try {
        if (getters.getPushNotificationsInitialized) return;
        if (isNativePlatform()) {
          await dispatch('nativePushNotifications');
        } else {
          await dispatch('webPushNotifications');
        }
      } catch (e) {
        console.error('customer initPushNotifications', e);
      }
    },
    updateMessageToken: async ({ getters }) => {
      if (!getters.getMessageToken) return;
      try {
        await api.post('/message', { messageToken: getters.getMessageToken });
      } catch (e) {
        console.error('customer updateMessageToken', e);
      }
    },
    webPushNotifications: async ({ commit, dispatch }) => {
      try {
        if (!('serviceWorker' in navigator) || !import.meta.env.VITE_FIREBASE_PUSH_CERT) return;
        const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
        const { app } = await import('@/utils/firebaseInitialize');
        await ensureFirebaseMessagingSw();
        const registration = await navigator.serviceWorker.ready;
        const permission = await Notification.requestPermission();
        if (permission !== 'granted') return;
        const messaging = getMessaging(app);
        const token = await getToken(messaging, {
          vapidKey: import.meta.env.VITE_FIREBASE_PUSH_CERT,
          serviceWorkerRegistration: registration,
        });
        if (!token) return;
        commit('SET_MESSAGE_TOKEN', token);
        await dispatch('updateMessageToken');
        onMessage(messaging, (payload) => {
          const action = payload?.data?.action;
          if (action) handlePushAction(action, { navigate: false });
        });
        commit('SET_PUSH_NOTIFICATION_INITIALIZED', true);
      } catch (e) {
        console.error('customer webPushNotifications', e);
      }
    },
    nativePushNotifications: async ({ commit, dispatch }) => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        let permStatus = await PushNotifications.checkPermissions();
        if (permStatus.receive === 'prompt') {
          permStatus = await PushNotifications.requestPermissions();
        }
        if (permStatus.receive !== 'granted') return;

        await PushNotifications.register();
        PushNotifications.addListener('registration', async (token) => {
          commit('SET_MESSAGE_TOKEN', token.value);
          await dispatch('updateMessageToken');
        });
        PushNotifications.addListener('registrationError', (error) => {
          console.error('customer push registrationError', error);
        });
        PushNotifications.addListener('pushNotificationReceived', (message) => {
          const action = message?.data?.action;
          if (action) handlePushAction(action, { navigate: false });
        });
        PushNotifications.addListener('pushNotificationActionPerformed', (message) => {
          const action = message?.notification?.data?.action;
          if (action) handlePushAction(action, { navigate: true });
        });
        commit('SET_PUSH_NOTIFICATION_INITIALIZED', true);
        await dispatch('nativeChannels');
      } catch (e) {
        console.error('customer nativePushNotifications', e);
      }
    },
    nativeChannels: async ({ commit, getters }) => {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        if (getters.getUserChannelCreated) return;
        commit('SET_USER_CHANNEL_CREATED', true);
        await PushNotifications.createChannel({
          id: 'General',
          description: 'General Channel',
          importance: 4,
          name: 'GeneralChannel',
          visibility: 1,
        });
      } catch (e) {
        console.error('customer nativeChannels', e);
      }
    },
  },
};
